// Turbo disables the submitter of the forms it submits and shows its data-turbo-submits-with.
// It leaves the data-turbo="false" forms to the browser (sign in, OAuth and payment redirects,
// the e-mail of a document): their submitter gets the same here, as rails-ujs gave it, so a
// double click cannot submit twice. So do the data-turbo="false" links that carry
// data-turbo-submits-with. Back to the page from the browser cache, they work again.

// A form submitter (<button>, <input>) or a link: only the former has a value and can be disabled.
type Submitter = HTMLElement & Partial<Pick<HTMLButtonElement, 'disabled' | 'value'>>

const disabled = new Map<Submitter, string>()

const leftToBrowser = (element: Element) => element.closest('[data-turbo="false"]')

function disable(element: Submitter) {
  const input = element.tagName === 'INPUT'
  disabled.set(element, input ? element.value! : element.innerHTML)
  const text = element.dataset.turboSubmitsWith
  if (text) input ? (element.value = text) : (element.textContent = text)

  if (element.tagName === 'A') {
    element.setAttribute('aria-disabled', 'true')
    element.classList.add('pointer-events-none', 'opacity-50')
  } else {
    element.disabled = true
  }
}

function restore(content: string, element: Submitter) {
  element.tagName === 'INPUT' ? (element.value = content) : (element.innerHTML = content)
  element.removeAttribute('aria-disabled')
  element.classList.remove('pointer-events-none', 'opacity-50')
  element.disabled = false
}

document.addEventListener('submit', event => {
  const { target: form, submitter } = event
  if (event.defaultPrevented || !submitter || !(leftToBrowser(submitter) || leftToBrowser(form as HTMLFormElement))) return

  // Once the form data is built: a submitter disabled now would leave it out.
  setTimeout(() => disable(submitter))
})

// A click that opens the link elsewhere (new tab or window, download) keeps this page as it is,
// so its link stays usable: the keys Turbo leaves to the browser, as rails-ujs did.
const opensElsewhere = (event: MouseEvent) =>
  event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey

document.addEventListener('click', event => {
  const link = (event.target as Partial<Element>).closest?.<HTMLAnchorElement>(
    'a[data-turbo="false"][data-turbo-submits-with]'
  )
  if (link && !event.defaultPrevented && !opensElsewhere(event)) setTimeout(() => disable(link))
})

window.addEventListener('pageshow', event => {
  if (!event.persisted) return

  disabled.forEach(restore)
  disabled.clear()
})
